export const AI_REVIEW_SETTINGS_ERROR_CODE = {
  notAuthenticated: 'not_authenticated',
  forbidden: 'forbidden',
  repositoryInstallationMismatch: 'repository_installation_mismatch',
  yamlManaged: 'yaml_managed',
  notFound: 'not_found',
  invalidInstallationId: 'invalid_installation_id',
  invalidJson: 'invalid_json',
  invalidParameters: 'invalid_parameters',
  validationFailed: 'validation_failed',
  fetchFailed: 'fetch_failed',
  updateFailed: 'update_failed',
  internalError: 'internal_error',
} as const;

export type AiReviewSettingsErrorCode =
  (typeof AI_REVIEW_SETTINGS_ERROR_CODE)[keyof typeof AI_REVIEW_SETTINGS_ERROR_CODE];

const AI_REVIEW_SETTINGS_ERROR_CODES = new Set<string>(
  Object.values(AI_REVIEW_SETTINGS_ERROR_CODE),
);

export function isAiReviewSettingsErrorCode(value: unknown): value is AiReviewSettingsErrorCode {
  return typeof value === 'string' && AI_REVIEW_SETTINGS_ERROR_CODES.has(value);
}

const DEFAULT_STATUS_BY_CODE: Record<AiReviewSettingsErrorCode, number> = {
  not_authenticated: 401,
  forbidden: 403,
  repository_installation_mismatch: 409,
  yaml_managed: 409,
  not_found: 404,
  invalid_installation_id: 400,
  invalid_json: 400,
  invalid_parameters: 400,
  validation_failed: 400,
  fetch_failed: 500,
  update_failed: 500,
  internal_error: 500,
};

type AiReviewSettingsErrorOptions = ErrorOptions & { status?: number };

export class AiReviewSettingsError extends Error {
  readonly code: AiReviewSettingsErrorCode;
  readonly status: number;

  constructor(
    code: AiReviewSettingsErrorCode,
    message: string,
    options: AiReviewSettingsErrorOptions = {},
  ) {
    super(message, options);
    this.name = 'AiReviewSettingsError';
    this.code = code;
    this.status = options.status ?? DEFAULT_STATUS_BY_CODE[code];
  }
}

export function isAiReviewSettingsError(error: unknown): error is AiReviewSettingsError {
  return error instanceof AiReviewSettingsError;
}

export function toError(error: unknown, fallbackMessage = 'Unexpected error'): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  return new Error(fallbackMessage);
}
